if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.hideErrors = this.dataset.hideErrors === 'true';

        // Initialize submit buttons
        this.updateSubmitButtons();
      }

      connectedCallback() {
        this.subscribeToVariantChanges = this.subscribeToVariantChanges.bind(this);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.subscribeToVariantChanges();
        } else {
          document.addEventListener('DOMContentLoaded', this.subscribeToVariantChanges, { once: true });
        }
      }

      disconnectedCallback() {
        document.removeEventListener('DOMContentLoaded', this.subscribeToVariantChanges);
        this.variantChangeUnsubscriber?.();
      }

      subscribeToVariantChanges() {
        if (!this.isConnected || this.variantChangeUnsubscriber || typeof subscribe !== 'function') return;

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange.bind(this));
      }

      // Method to collect all submit buttons (can be called multiple times)
      updateSubmitButtons() {
        const section = this.closest('.shopify-section');
        const forms =
          section && this.dataset.productId
            ? Array.from(section.querySelectorAll('product-form')).filter(
                (form) => form.dataset.productId === this.dataset.productId,
              )
            : [this];
        this.submitButtons = forms.flatMap((form) =>
          Array.from(form.querySelectorAll('.product-form__submit, button[name="add"]')),
        );

        this.submitButtons.forEach((btn) => {
          const span = btn.querySelector('span');
          if (span) btn.submitButtonText = span;
          if (document.querySelector('cart-drawer')) btn.setAttribute('aria-haspopup', 'dialog');
        });
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        // Refresh button list before checking
        this.updateSubmitButtons();

        // Block if any button is disabled
        if (this.submitButtons.some((btn) => btn.getAttribute('aria-disabled') === 'true')) return;

        this.handleErrorMessage();

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const submittedVariantId = formData.get('id');
        const previousCartQuantity = this.getCartQuantity();
        const inventoryAdjustment = this.adjustQuantityToAvailableInventory(formData);

        if (inventoryAdjustment && inventoryAdjustment.quantity === 0) {
          this.handleErrorMessage(inventoryAdjustment.message, 'notice');
          return;
        }

        this.submitButtons.forEach((btn) => {
          btn.setAttribute('aria-disabled', true);
          btn.classList.add('loading');
          const spinner = btn.querySelector('.loading__spinner');
          if (spinner) spinner.classList.remove('hidden');
        });

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id),
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              cartData: response,
            });

            this.setCartQuantity(
              previousCartQuantity + Number.parseInt(formData.get('quantity') || '1', 10),
              submittedVariantId,
            );

            if (inventoryAdjustment) {
              this.handleErrorMessage(inventoryAdjustment.message, 'notice');
            }

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true },
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage('We could not add this item to your cart. Please try again.');
          })
          .finally(() => {
            this.submitButtons.forEach((btn) => {
              btn.classList.remove('loading');
              if (!btn.disabled && !btn.classList.contains('notify-me-button')) {
                btn.removeAttribute('aria-disabled');
              }
              const spinner = btn.querySelector('.loading__spinner');
              if (spinner) spinner.classList.add('hidden');
            });

            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          });
      }

      adjustQuantityToAvailableInventory(formData) {
        if (this.dataset.inventoryTracked !== 'true') return null;

        const inventoryQuantity = Number.parseInt(this.dataset.inventoryQuantity, 10);
        const requestedQuantity = Number.parseInt(formData.get('quantity') || '1', 10);

        if (!Number.isFinite(inventoryQuantity) || !Number.isFinite(requestedQuantity)) return null;

        const availableQuantity = Math.max(inventoryQuantity - this.getCartQuantity(), 0);

        if (requestedQuantity <= availableQuantity) return null;

        if (availableQuantity === 0) {
          return {
            quantity: 0,
            message: 'All available stock is already in your cart.',
          };
        }

        formData.set('quantity', String(availableQuantity));

        return {
          quantity: availableQuantity,
          message: `Only ${availableQuantity} ${availableQuantity === 1 ? 'item was' : 'items were'} available. ${availableQuantity} ${availableQuantity === 1 ? 'item has' : 'items have'} been added to your cart.`,
        };
      }

      getCartQuantity() {
        const formId = this.form.getAttribute('id');
        const quantityInput =
          this.form.querySelector('[name="quantity"]') ||
          (formId ? document.querySelector(`[name="quantity"][form="${formId}"]`) : null);
        const cartQuantity = Number.parseInt(
          quantityInput?.dataset.cartQuantity ?? this.dataset.cartQuantity ?? '0',
          10,
        );

        return Number.isFinite(cartQuantity) ? cartQuantity : 0;
      }

      setCartQuantity(cartQuantity, variantId = this.variantIdInput.value) {
        const section = this.closest('.shopify-section');
        const forms = section ? Array.from(section.querySelectorAll('product-form')) : [this];
        forms.forEach((form) => {
          if (form.dataset.productId !== this.dataset.productId || form.variantIdInput?.value !== variantId) return;
          form.dataset.cartQuantity = String(cartQuantity);
          const formId = form.form.getAttribute('id');
          const quantityInput =
            form.form.querySelector('[name="quantity"]') ||
            (formId ? document.querySelector(`[name="quantity"][form="${formId}"]`) : null);
          if (quantityInput) quantityInput.dataset.cartQuantity = String(cartQuantity);
        });
        section?.querySelectorAll('.color-input[data-variant-id]').forEach((input) => {
          if (input.dataset.productId === this.dataset.productId && input.dataset.variantId === variantId) {
            input.dataset.cartQuantity = String(cartQuantity);
          }
        });
      }

      handleVariantChange({ data }) {
        if (data.sectionId !== this.dataset.sectionId) return;

        const productSelector = this.dataset.productId ? `[data-product-id="${this.dataset.productId}"]` : '';
        const updatedProductForm = data.html.querySelector(
          `product-form[data-section-id="${this.dataset.sectionId}"]${productSelector}`,
        );

        if (!updatedProductForm) return;

        this.dataset.inventoryTracked = updatedProductForm.dataset.inventoryTracked;
        this.dataset.inventoryQuantity = updatedProductForm.dataset.inventoryQuantity;
        this.dataset.cartQuantity = updatedProductForm.dataset.cartQuantity;
      }

      handleErrorMessage(errorMessage = false, messageType = 'error') {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;

        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
        this.errorMessageWrapper.dataset.messageType = errorMessage ? messageType : '';

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      setPendingState() {
        this.updateSubmitButtons();

        if (!this.pendingButtonStates) this.pendingButtonStates = new Map();

        this.submitButtons.forEach((btn) => {
          if (!this.pendingButtonStates.has(btn)) {
            this.pendingButtonStates.set(btn, {
              type: btn.getAttribute('type'),
              id: btn.getAttribute('id'),
              ariaDisabled: btn.getAttribute('aria-disabled'),
              ariaBusy: btn.getAttribute('aria-busy'),
              disabled: btn.disabled,
              notifyMe: btn.classList.contains('notify-me-button'),
              text: btn.submitButtonText?.textContent ?? btn.textContent,
            });
          }

          btn.disabled = true;
          btn.setAttribute('type', 'button');
          btn.removeAttribute('id');
          btn.setAttribute('aria-disabled', 'true');
          btn.setAttribute('aria-busy', 'true');
          btn.classList.remove('notify-me-button');
          btn.classList.add('is-variant-loading');
        });
      }

      clearPendingState() {
        this.submitButtons?.forEach((btn) => {
          btn.classList.remove('is-variant-loading');
          btn.removeAttribute('aria-busy');
        });
        this.pendingButtonStates = null;
      }

      restorePendingState() {
        if (!this.pendingButtonStates) return;

        this.pendingButtonStates.forEach((state, btn) => {
          if (!btn.isConnected) return;

          if (state.type === null) btn.removeAttribute('type');
          else btn.setAttribute('type', state.type);

          if (state.id === null) btn.removeAttribute('id');
          else btn.setAttribute('id', state.id);

          if (state.ariaDisabled === null) btn.removeAttribute('aria-disabled');
          else btn.setAttribute('aria-disabled', state.ariaDisabled);

          if (state.ariaBusy === null) btn.removeAttribute('aria-busy');
          else btn.setAttribute('aria-busy', state.ariaBusy);

          btn.disabled = state.disabled;
          btn.classList.toggle('notify-me-button', state.notifyMe);
          btn.classList.remove('is-variant-loading');

          if (btn.submitButtonText) {
            btn.submitButtonText.textContent = state.text;
          } else {
            btn.textContent = state.text;
          }
        });

        this.pendingButtonStates = null;
      }

      toggleSubmitButton(disable = true) {
        // Refresh the button list before toggling
        this.updateSubmitButtons();
        this.clearPendingState();
        // Find the active variant input containing the data attributes
        const currentVariantId = this.variantIdInput?.value;
        const activeVariantInput =
          document.querySelector(`input[data-variant-id="${currentVariantId}"]`) ||
          document.querySelector(`input[form="${this.form.getAttribute('id')}"]:checked`);

        this.submitButtons.forEach((btn) => {
          btn.disabled = false;
          btn.removeAttribute('aria-disabled');
          btn.removeAttribute('aria-busy');

          if (disable) {
            // Notify Me
            btn.setAttribute('type', 'button');
            btn.setAttribute('id', 'notifyBtn');
            btn.classList.add('notify-me-button');
            if (btn.submitButtonText) {
              btn.submitButtonText.textContent = 'Notify Me';
            } else {
              btn.textContent = 'Notify Me';
            }

            // Assign data attributes from the selected variant input, fallback to blank if missing
            if (activeVariantInput) {
              btn.dataset.variantId = activeVariantInput.dataset.variantId || '';
              btn.dataset.productId = activeVariantInput.dataset.productId || '';
              btn.dataset.variantTitle = activeVariantInput.dataset.variantTitle || '';
              btn.dataset.productTitle = activeVariantInput.dataset.productTitle || '';
              btn.dataset.productImage = activeVariantInput.dataset.variantImage || ''; // Maps 'data-variant-image' to 'dataset.productImage'
              btn.dataset.variantImage = activeVariantInput.dataset.variantImage || ''; // Maps 'data-variant-image' to 'dataset.productImage'
            } else {
              btn.dataset.variantId = '';
              btn.dataset.productId = '';
              btn.dataset.variantTitle = '';
              btn.dataset.productTitle = '';
              btn.dataset.productImage = '';
              btn.dataset.variantImage = '';
            }
          } else {
            // Add to Cart
            btn.setAttribute('type', 'submit');
            btn.dataset.variantId = '';
            btn.dataset.productId = '';
            btn.dataset.variantTitle = '';
            btn.dataset.productTitle = '';
            btn.dataset.productImage = '';
            btn.dataset.variantImage = '';
            btn.removeAttribute('id');
            btn.classList.remove('notify-me-button');
            if (btn.submitButtonText) {
              btn.submitButtonText.textContent = window.variantStrings.addToCart;
            } else {
              btn.textContent = window.variantStrings.addToCart;
            }
          }
        });
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    },
  );
}
